import pickBy from "lodash-es/pickBy";
import { GameLaunchStatus } from "@onmo/eon-streaming";
import {
  AnalyticsEvent,
  GameOverlayActionSource,
  GameOverlayActionType,
  GTM_ID,
  IAnalyticsParams,
} from "../types/analyticsType";
import { getGamepadsConnected } from "./gamepad";
import { NetworkConditionStatus } from "../types/network";

export const STATUS_IN_QUEUE = "STATUS_IN_QUEUE";

const GAME_LAUNCH_STATUS_EVENT_MAP: Partial<Record<string, AnalyticsEvent>> = {
  [GameLaunchStatus.STATUS_STARTING]: AnalyticsEvent.GameLaunchStarting,
  [GameLaunchStatus.STATUS_STEAMLOGIN]: AnalyticsEvent.GameLaunchSteamLogin,
  [GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS]:
    AnalyticsEvent.GameLaunchSteamLoginSuccess,
  [GameLaunchStatus.STATUS_EULA]: AnalyticsEvent.GameLaunchEula,
  [GameLaunchStatus.STATUS_GAMELAUNCHING]:
    AnalyticsEvent.GameLaunchGameLaunching,
  [GameLaunchStatus.STATUS_GAMELAUNCH_TIMEOUT]:
    AnalyticsEvent.GameLaunchGameLaunchTimeout,
  [GameLaunchStatus.STATUS_GAMERUNNING]: AnalyticsEvent.GameLaunchGameRunning,
  [GameLaunchStatus.STATUS_GAMEEXITED]: AnalyticsEvent.GameLaunchGameExited,
  [STATUS_IN_QUEUE]: AnalyticsEvent.GameLaunchInQueue,
  [GameLaunchStatus.ERR_NOTACTIVE]: AnalyticsEvent.GameLaunchNotActive,
  [GameLaunchStatus.STATUS_LAUNCHING_DISCONNECTED]:
    AnalyticsEvent.GameLaunchLaunchingDisconnected,
  [GameLaunchStatus.STATUS_INACTIVITY_TIMEOUT]:
    AnalyticsEvent.GameLaunchInactivityTimeout,
  [GameLaunchStatus.STATUS_GAMERUNNING_UPDATEPENDING]:
    AnalyticsEvent.GameLaunchGameRunningUpdatePending,
  [GameLaunchStatus.STATUS_GAMERUNNING_DISCONNECTED]:
    AnalyticsEvent.GameLaunchGameRunningDisconnected,
  [GameLaunchStatus.STATUS_CLIENTDISCONNECTED]:
    AnalyticsEvent.GameLaunchClientDisconnected,
  [GameLaunchStatus.STATUS_UPDATEDETECTED]:
    AnalyticsEvent.GameLaunchUpdateDetected,
};

const NETWORK_PERFORMANCE_WARNING_TYPE: Record<
  NetworkConditionStatus,
  "latency" | "bandwidth" | "jitter" | "packet_loss"
> = {
  [NetworkConditionStatus.PACKET_LOSS]: "packet_loss",
  [NetworkConditionStatus.HIGH_LATENCY]: "latency",
  [NetworkConditionStatus.LATENCY_VARIATION]: "jitter",
  [NetworkConditionStatus.BANDWIDTH]: "bandwidth",
};

const trackEvent = (params: IAnalyticsParams): void => {
  const userId =
    sessionStorage.getItem("user_id") || params?.user_id || "visitor";
  try {
    window.dataLayer = window.dataLayer || [];
    const dataLayerPayload = {
      event: `onmo.${params.event_name}`,
      user_id: userId,
      ui_nu: userId,
      user_properties: params.user_properties,
      source: params.source,
      type: params.type,
      value: params.value,
      game_session_id: params.game_session_id,
      session_time: params.session_time,
      status: params.status,
      error: params.error,
      gtmId: GTM_ID,
    };

    const cleanedPayload = pickBy(
      dataLayerPayload,
      (v) => v !== undefined && v !== null && v !== "",
    );

    // do not delete this log
    console.log(`[Analytics-[${GTM_ID}]] dataLayer: `, cleanedPayload);

    window.dataLayer.push(cleanedPayload);
  } catch (err) {
    console.warn("Analytics push failed", err);
  }
};

export const AnalyticsGA4 = {
  // --- AUTH & ONBOARDING ---
  onboardingStart: (
    source: "activate_qr" | "activate_url" | "tv_qr" | "tv_url" | "organic",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.OnboardingStart,
      user_properties: { sub_status: "guest" },
      source: source,
    });
  },

  authSkip: (
    source: "activate_qr" | "activate_url" | "tv_qr" | "tv_url" | "organic",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.AuthSkip,
      source: source,
    });
  },

  authIntent: (source: "onboarding" | "console" | "paywall") => {
    trackEvent({
      event_name: AnalyticsEvent.AuthIntent,
      source: source,
    });
  },

  authOtp: (
    status: "success" | "error",
    error?: "invalid_code" | "expired" | "network_error",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.AuthOtp,
      status: status,
      error: error,
    });
  },

  // --- SUBSCRIPTION & PAYWALL ---
  subscriptionStatus: (
    user_id: IAnalyticsParams["user_id"],
    status: "inactive" | "active" | "expired",
    sub_status?: string,
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.SubscriptionStatus,
      user_id: user_id,
      user_properties: { sub_status: sub_status },
      source: "backend",
      status: status,
    });
  },

  subscriptionTransaction: (
    source: "paywall" | "settings" | "console" | "game",
    status: "success" | "failure" | "cancel" | "processing",
    type: "plan_type",
    sub_status?: string,
    error?: string,
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.SubscriptionTransaction,
      source: source,
      status: status,
      type: type,
      user_properties: { sub_status: sub_status },
      error: error,
    });
  },

  bundleCodeActivate: (
    source: "qr" | "manual",
    value: IAnalyticsParams["value"],
    status: "success" | "error",
    error?: "already_subscribed" | "already_used" | "invalid" | "expired",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.BundleCodeActivate,
      user_properties: { sub_status: "active" },
      source: source,
      value: value, // bundle name
      status: status,
      error: error,
    });
  },

  paywallView: (
    source: "onboarding" | "console" | "game",
    type: "no_subscription" | "expired",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.PaywallView,
      source: source,
      type: type,
    });
  },

  paywallSelect: (type: "bundle" | "subscribe" | "activate" | "skip") => {
    trackEvent({
      event_name: AnalyticsEvent.PaywallSelect,
      source: "paywall",
      type: type,
    });
  },

  // --- TV LINKING ---
  tvLinkGrant: (
    source: "web" | "mobile_app",
    type: "qr_scan" | "manual_entry",
    status: "success" | "error",
    error?: "denied" | "network_error",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.TvLinkGrant,
      source: source,
      type: type,
      status: status,
      error: error,
    });
  },

  tvLinkAttempt: (
    source: "web" | "mobile_app",
    type: "qr_scan" | "manual_entry",
    status: "success" | "error",
    error?: "invalid_code" | "expired" | "network_error",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.TvLinkAttempt,
      source: source,
      type: type,
      status: status,
      error: error,
    });
  },

  // --- NAVIGATION & PROFILE ---
  profileSetup: (value: IAnalyticsParams["value"]) => {
    trackEvent({
      event_name: AnalyticsEvent.ProfileSetup,
      source: "profile_setup",
      value: value, // avatar name
    });
  },

  homepageAction: (
    type:
      | "console"
      | "controller"
      | "activity"
      | "apps"
      | "byog"
      | "essential_pass",
  ) => {
    const controllers = getGamepadsConnected().length;
    trackEvent({
      event_name: AnalyticsEvent.HomepageAction,
      user_properties: { controllers: controllers },
      type: type,
    });
  },

  navbarAction: (type: "console" | "essentials" | "byog") => {
    const controllers = getGamepadsConnected().length;
    trackEvent({
      event_name: AnalyticsEvent.NavbarAction,
      user_properties: { controllers: controllers },
      type: type,
    });
  },

  // --- GAME ACTIONS ---
  gameSelect: (
    source: "recent" | "console" | "essentials" | "byog",
    type: "essentials" | "byog",
    value: IAnalyticsParams["value"],
  ) => {
    const controllers = getGamepadsConnected().length;
    trackEvent({
      event_name: AnalyticsEvent.GameSelect,
      user_properties: { controllers: controllers },
      source: source,
      type: type,
      value: value, // game name
    });
  },

  gameBuy: (value: IAnalyticsParams["value"], controllers: number) => {
    trackEvent({
      event_name: AnalyticsEvent.GameBuy,
      user_properties: { controllers: controllers },
      value: value, // game name
    });
  },

  gameViewIgdb: (value: IAnalyticsParams["value"], controllers: number) => {
    trackEvent({
      event_name: AnalyticsEvent.GameViewIgdb,
      user_properties: { controllers: controllers },
      value: value, // game name
    });
  },

  // --- GAME LAUNCH FLOW ---
  gameLaunchStart: (
    type: "essentials" | "byog",
    value: IAnalyticsParams["value"],
    game_session_id: IAnalyticsParams["game_session_id"],
    status: "success" | "error",
    error?: "maintenance" | "no_subscription",
  ) => {
    const controllers = getGamepadsConnected().length;
    trackEvent({
      event_name: AnalyticsEvent.GameLaunchStart,
      user_properties: { controllers: controllers },
      type: type,
      value: value, // game name
      game_session_id: game_session_id,
      status: status,
      error: error,
    });
  },

  gameLaunchStatus: (
    status: string,
    type: "essentials" | "byog",
    value: IAnalyticsParams["value"],
    game_session_id: IAnalyticsParams["game_session_id"],
    session_time: IAnalyticsParams["session_time"],
  ) => {
    const event_name = GAME_LAUNCH_STATUS_EVENT_MAP[status];
    if (!event_name) return;

    const controllers = getGamepadsConnected().length;
    trackEvent({
      event_name: event_name,
      user_properties: { controllers: controllers },
      type: type,
      value: value, // game name
      game_session_id: game_session_id,
      session_time: session_time,
    });
  },

  // --- IN-GAME & END SESSION ---
  gameOverlayAction: (
    source: GameOverlayActionSource,
    type: GameOverlayActionType,
    value: IAnalyticsParams["value"],
    game_session_id: IAnalyticsParams["game_session_id"],
    session_time: IAnalyticsParams["session_time"],
    controllers: number,
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.GameOverlayAction,
      user_properties: { controllers: controllers },
      source: source,
      type: type,
      value: value,
      game_session_id: game_session_id,
      session_time: session_time,
    });
  },

  gamePerformanceWarning: (
    status: NetworkConditionStatus,
    value: IAnalyticsParams["value"],
    game_session_id: IAnalyticsParams["game_session_id"],
    session_time: IAnalyticsParams["session_time"],
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.GamePerformanceWarning,
      // Map back to the legacy analytics `type` so the GA4 contract is unchanged.
      type: NETWORK_PERFORMANCE_WARNING_TYPE[status],
      value: value,
      game_session_id: game_session_id,
      session_time: session_time,
    });
  },

  gameSessionEnd: (
    type: "essentials" | "byog",
    value: IAnalyticsParams["value"],
    game_session_id: IAnalyticsParams["game_session_id"],
    session_time: IAnalyticsParams["session_time"],
    status: "quit" | "crash" | "timeout",
  ) => {
    trackEvent({
      event_name: AnalyticsEvent.GameSessionEnd,
      type: type,
      value: value,
      game_session_id: game_session_id,
      session_time: session_time,
      status: status,
    });
  },

  cancelSubscription: (type: string | null, message?: string) => {
    if (!type) return;
    trackEvent({
      event_name: AnalyticsEvent.SubscriptionCancel,
      type,
      value: message,
    });
  },
};
